import { useAuth0 } from '@auth0/auth0-react';
import { Login, LoginUserListItemDto } from 'features/auth/types';
import { fetchCurrentLoginUser, fetchLoginUsers } from '../utils/federatedApi';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNotification } from 'features/notifications';
import { useContext } from 'react';
import { AuthTokenContext } from './authContext';
import { SelectedUserContext } from './selectedUserContext';
import { clearTableStateStorage } from 'lib/storage/clearTableStateStorage';

const userKey = 'federated-current-user';
const loginUsersKey = 'federated-login-users';

export const useAuthState = () => useContext(AuthTokenContext);
export const useSelectedUser = () => useContext(SelectedUserContext);

export const useLoginUsers = () => {
  const { isAuthenticated, isLoading, token } = useAuthState();
  const enabled = !isLoading && isAuthenticated && !!token;
  return useQuery<LoginUserListItemDto[], Error>({
    queryKey: [loginUsersKey],
    queryFn: async () => await fetchLoginUsers(token ?? ''),
    enabled,
  });
};

export const useUser = () => {
  const { isAuthenticated, isLoading, token } = useAuthState();
  const { selectedUserId } = useSelectedUser();
  const enabled = !isLoading && isAuthenticated && !!token && !!selectedUserId;
  return useQuery<Login, Error>({
    queryKey: [userKey],
    queryFn: async () => await fetchCurrentLoginUser(token ?? '', selectedUserId ?? ''),
    enabled,
  });
};

export const useLogout = (): (() => Promise<void>) => {
  const { logout } = useAuth0();
  const queryClient = useQueryClient();
  const { dismissNotification } = useNotification();
  const { clearSelectedUser } = useSelectedUser();

  return async () => {
    queryClient.removeQueries({ queryKey: [userKey] });
    queryClient.removeQueries({ queryKey: [loginUsersKey] });
    dismissNotification();
    clearTableStateStorage();
    clearSelectedUser();
    await logout({ logoutParams: { returnTo: window.location.origin } });
  };
};

export const useLogin = (): (() => Promise<void>) => {
  const { loginWithRedirect, error } = useAuth0();
  const queryClient = useQueryClient();
  const { notify, dismissNotification } = useNotification();
  const { clearSelectedUser } = useSelectedUser();

  return async () => {
    queryClient.removeQueries({ queryKey: [userKey] });
    queryClient.removeQueries({ queryKey: [loginUsersKey] });
    dismissNotification();
    if (error) notify.error({ id: 'error', message: error?.message });
    clearSelectedUser();
    await loginWithRedirect();
  };
};
